import React from 'react';
    import { motion } from 'framer-motion';
    import { useGame } from '../context/GameContext';
    import Card from '../components/ui/Card';
    import Button from '../components/ui/Button';
    import ProgressRing from '../components/ui/ProgressRing';
    import SafeIcon from '../components/common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { GAME_CONFIG } from '../config/constants';
    import { GameService } from '../services/gameService';
    import { formatNumber } from '../utils/formatters';
    
    const { FiZap, FiTrendingUp, FiRepeat, FiAward } = FiIcons;
    
    export default function PrestigePage() {
      const { state, actions } = useGame();
      const prestigeRequirement = GameService.calculatePrestigeRequirement(state.prestigeLevel);
      const prestigeProgress = (state.seeds / prestigeRequirement) * 100;
      const canPrestige = state.seeds >= prestigeRequirement;
    
      const nextCompost = Math.min(
        state.compost + GAME_CONFIG.prestige.compostBonus,
        GAME_CONFIG.prestige.maxMultiplier
      );
    
      return (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-charcoal mb-2"> Prestige </h1>
            <p className="text-charcoal/60 font-dm-sans"> Reset your progress for powerful permanent boosts. </p>
          </div>
    
          <Card className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-cormorant font-bold text-charcoal mb-2"> Prestige Progress </h2>
              <p className="text-charcoal/60 font-dm-sans">
                {canPrestige ? "You are ready to prestige!" : `You need ${formatNumber(prestigeRequirement - state.seeds)} more seeds.`}
              </p>
            </div>
            <div className="flex justify-center mb-8">
              <ProgressRing
                progress={Math.min(prestigeProgress, 100)}
                size={180}
                strokeWidth={12}
                color={canPrestige ? "#E2A93B" : "#9BB898"}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-charcoal">
                    {Math.round(prestigeProgress)}%
                  </div>
                  <div className="text-sm text-charcoal/60">
                    {canPrestige ? "Ready!" : "Progress"}
                  </div>
                </div>
              </ProgressRing>
            </div>
            <div className="text-center">
              <Button
                variant="warning"
                onClick={actions.prestige}
                disabled={!canPrestige}
                size="lg"
                className="px-8"
                icon={FiZap}
              >
                Prestige Now
              </Button>
              {!canPrestige && (
                <p className="text-sm text-crimson mt-4">
                  You have not met the requirements to prestige.
                </p>
              )}
            </div>
          </Card>
    
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-cormorant font-bold text-charcoal mb-4 text-xl">What You Gain</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-sage/10 rounded-lg">
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-sage"/>
                  <div>
                    <p className="font-semibold">New Compost Multiplier: <span className="text-sage">{nextCompost.toFixed(2)}x</span></p>
                    <p className="text-xs text-charcoal/60">From {state.compost.toFixed(2)}x</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-goldenrod/10 rounded-lg">
                  <SafeIcon icon={FiAward} className="w-5 h-5 text-goldenrod"/>
                  <p>Prestige Level {state.prestigeLevel + 1}</p>
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="font-cormorant font-bold text-charcoal mb-4 text-xl">What Resets</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-crimson/5 rounded-lg">
                  <SafeIcon icon={FiRepeat} className="w-5 h-5 text-crimson/80"/>
                  <p>Seeds, Planters, Gardeners, Upgrades</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-skyblue/10 rounded-lg">
                  <SafeIcon icon={FiZap} className="w-5 h-5 text-skyblue/80"/>
                  <p>Permanent SPS from mentors is kept</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      );
    }